import { Show, createSignal, createMemo, onCleanup, batch } from 'solid-js'
import { Portal } from 'solid-js/web'
import type { JSX } from 'solid-js'
import { SelectableObject } from '../interactions/SelectableObject'
import { Placeholder } from '../Placeholder'
import { afterRender, onDocumentEvent } from '../utils'
import { IRenderContext } from 'Shared/posters/canvas/Object'
import { createImageGeometry, createNaturalSize } from './imageGeometry'
import { ImageImpl } from 'Shared/posters/canvas/images/ImageImpl'
import { useUser } from 'Shared/SiteProvider'
import { GeometrySeed, adoptDesignGeometry, defaultSeedBox, type Dimensions } from 'Shared/posters/canvas/interactions/ObjectImpl'
import { cloneBox, type Box } from '../../types'
import { createStore } from 'solid-js/store'
import { resolveEffectiveStyle } from 'Shared/posters/project/themeVariables'
import { extractCorners, imageFilterCss } from 'Shared/posters/canvas/styleAttrs'
import { ClipRect } from 'Shared/posters/canvas/RoundedRect'

export function ImageObject(P: IRenderContext)
{
	const selCtx    = P.selection;
	const histCtx   = P.history;
	const objCtx    = P.objects;
	const uploadCtx = P.upload;
	const config    = P.images;
	let hitRectRef:   SVGRectElement | undefined;
	let fileInputRef: HTMLInputElement | undefined;

	const [pan, setPan]         = createSignal(P.object.crop?.pan ?? { x: 0, y: 0 });
	const [zoom, setZoomRaw]    = createSignal(P.object.crop?.zoom ?? 1);
	const [stretch, setStretch] = createSignal(P.object.crop?.stretch ?? 1);
	const [imgEl, setImgEl]     = createSignal<HTMLImageElement | null>(null);
	const [shiftDragging, setShiftDragging]   = createSignal(false);
	const [isEditingField, setIsEditingField] = createSignal(false);

	const seed = (): GeometrySeed => ({
		box: {
			pos:  P.object.position ?? defaultSeedBox.pos,
			dims: {
				width:  P.object.size?.width  ?? P.canvas.width,
				height: P.object.size?.height ?? P.canvas.height,
			},
		},
		layoutPos: P.layoutPos,
	});
	const [pos, setPos] = createStore(cloneBox(seed().box));

	// Zoom about the centre: scale the existing pan by the same factor so the focused point stays put.
	const setZoom = (newZoom: number) => {
		const oldZoom = zoom();
		batch(() => {
			setPan((p: { x: number; y: number }) => ({ x: p.x * newZoom / oldZoom, y: p.y * newZoom / oldZoom }));
			setZoomRaw(newZoom);
		});
	};

	// The design geometry this store was last seeded/adopted from — see adoptDesignGeometry. An
	// image's seed carries no measurement, so it doubles as the design box.
	const adopted: { current: Box | null } = { current: null };

	const impl = createMemo(() => {
		const g = seed();
		const created = new ImageImpl(
			pos, setPos, g,
			pan, setPan,
			zoom, setZoom,
			stretch, setStretch,
			imgEl, setImgEl,
			shiftDragging, setShiftDragging,
			isEditingField, setIsEditingField,
			config,
			P.object,
			P.canvas,
			useUser,
			P.record,
			P.theme,
			() => fileInputRef
		);
		adoptDesignGeometry(created, g.box, g, adopted);
		return created;
	});


	// A shift-drag crop ends on mouseup anywhere, even outside the image. A pure passthrough to the
	// persistent signal, so this writes setShiftDragging directly rather than round-tripping through impl().
	onDocumentEvent('mouseup', () => setShiftDragging(false));

	// Resolved display URL; memoised so the field/binding lookup runs once per record change.
	const effectiveSrc = createMemo(() => impl().effectiveSrc());

	// CSS filter string (brightness/saturate/blur/grayscale/sepia), with theme-variable
	// bindings folded in so a bound filter renders the variable's value.
	const filterCss = () => imageFilterCss(resolveEffectiveStyle(P.object, P.theme));

	// Per-corner radii, applied by clipping the image to a rounded outline.
	const corners = () => extractCorners(resolveEffectiveStyle(P.object, P.theme));

	// Natural size of the loaded image; resets the crop/zoom view whenever the source changes.
	// The reset fires at the START of every load — including the first — so skip that first load
	// or it would wipe the crop restored from the saved object (the signals are seeded from P.object.crop).
	let viewLoaded = false;
	const natSize = createNaturalSize(effectiveSrc, () => {
		if (!viewLoaded) { viewLoaded = true; return; }
		setZoom(1);
		setStretch(1);
		setPan({ x: 0, y: 0 });
		// The reset only touches signals, so mark the history dirty to schedule a save — otherwise
		// a stale crop from the replaced image could linger in the DB (serializeObjs reads the live
		// crop, but nothing here would trigger AutoSave on its own).
		histCtx?.markDirty();
	});

	const clipId = `clip-${P.object.id}`;

	return (
		<SelectableObject
			objects={objCtx}
			selection={selCtx}
			history={histCtx}
			drag={P.drag}
			impl={impl()}
			canvas={P.canvas}
			design={P.design}
			parentOffset={P.parentOffset} parentId={P.parentId}
			mode={P.mode}
		>
			{(dims: () => Dimensions) => {
				// zoom/stretch/pan are read as the component's signals directly (not via impl()) — they're
				// the stable persistent accessors themselves, not a record/object-derived read, so there's
				// nothing to re-derive.
				const geom = createImageGeometry({
					natSize,
					frame:   dims,
					zoom,
					stretch,
					pan,
				})
				return (
					<>
						<defs>
							<clipPath id={clipId}>
								<ClipRect width={dims().width} height={dims().height} corners={corners()} />
							</clipPath>
						</defs>

						<Show when={!effectiveSrc()}>
							<Placeholder id={P.object.id} width={dims().width} height={dims().height} />
						</Show>

						<g clip-path={`url(#${clipId})`}>
							<image
								ref={el => setImgEl(el as unknown as HTMLImageElement)}
								href={effectiveSrc() ?? undefined}
								x={geom.imgAttrs().box.pos.x} y={geom.imgAttrs().box.pos.y}
								width={geom.imgAttrs().box.dims.width} height={geom.imgAttrs().box.dims.height}
								preserveAspectRatio={geom.imgAttrs().ar}
								style={filterCss() ? { filter: filterCss()! } : {}}
							/>
						</g>

						{/* Positioning target for the field-name overlay (host owns hit/selection). */}
						<rect ref={el => hitRectRef = el} x={0} y={0} width={dims().width} height={dims().height} fill="transparent" pointer-events="none" />

						<Show when={isEditingField()}>
							<FieldNameOverlay
								targetRef={() => hitRectRef}
								initialValue={P.object.field ?? ''}
								onDone={value => impl().finishFieldEdit(value, objCtx, histCtx, selCtx)}
							/>
						</Show>

						<Portal>
							<input ref={el => fileInputRef = el} type="file" accept="image/*" style="display:none" onChange={e => impl().handleFileChange(e, objCtx, histCtx, uploadCtx)} />
						</Portal>
					</>
				)
			}}
		</SelectableObject>
	);
}

// ── FieldNameOverlay ─────────────────────────────────────────────────────────

interface FieldNameOverlayProps {
	targetRef:    () => SVGRectElement | undefined
	initialValue: string
	onDone:       (value: string) => void
}

function FieldNameOverlay(P: FieldNameOverlayProps): JSX.Element {
	let input: HTMLInputElement | undefined;
	const [style, setStyle] = createSignal<Record<string, string>>({});
	let saved = false;

	const save = () => {
		if (saved) return
		saved = true
		P.onDone(input?.value ?? '')
	};

	onCleanup(save);

	// afterRender already defers past mount (the Portal'd input and target rect are
	// attached by then), so no onMount wrapper is needed.
	afterRender(() => {
		const el = P.targetRef()
		if (el) {
			const bbox = el.getBoundingClientRect()
			setStyle({
				position:         'fixed',
				left:             Math.max(0, bbox.left) + 'px',
				top:              Math.max(0, bbox.top + bbox.height / 2 - 28) + 'px',
				width:            Math.max(160, bbox.width) + 'px',
				padding:          '6px 8px',
				background:       'rgba(0,0,0,.85)',
				border:           '2px solid #4d9eff',
				'border-radius':  '3px',
				'z-index':        '99999',
				display:          'flex',
				'flex-direction': 'column',
				gap:              '4px',
				'box-sizing':     'border-box',
			})
		}
		input?.focus();
		input?.select();
	});

	return (
		<Portal>
			<div style={style()} data-keeps-selection=''>
				<span style={{ color: '#aaa', 'font-size': '11px', 'font-family': 'sans-serif' }}>Field</span>
				<input
					ref={el => input = el}
					type="text"
					value={P.initialValue}
					style={{
						background:      'transparent',
						border:          'none',
						'border-bottom': '1px solid #4d9eff',
						outline:         'none',
						color:           '#fff',
						'font-size':     '14px',
						'font-family':   'sans-serif',
						padding:         '2px 0',
						width:           '100%',
					}}
					onKeyDown={(e: KeyboardEvent) => {
						if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); save() }
					}}
					onBlur={save}
				/>
			</div>
		</Portal>
	)
}
